import React, { useMemo, useState } from "react";
import { useT, useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import { fmtINR, FD_REGULAR, FD_SENIOR, MIP_REGULAR, MIP_SENIOR, RD_PLAN } from "../data/schemes";
import { TID } from "../constants/testIds";

// FD compound annually
const calcFD = (P, months, ratePA) => {
    const r = ratePA / 100;
    const fullYears = Math.floor(months / 12);
    const remMonths = months - fullYears * 12;
    // compound for full years, then add simple interest for remainder months on compounded value
    let amount = P * Math.pow(1 + r, fullYears);
    if (remMonths > 0) {
        amount = amount * (1 + (r * remMonths) / 12);
    }
    return amount;
};

// MIP monthly income: interest compounds quarterly, paid out at its monthly-equivalent rate
// Monthly Income Deposit
const calcMIP = (P, ratePA) => {
    return (P * (ratePA / 100)) / 12;
};
// RD maturity using standard formula M = P * [((1+i)^n - 1) / (1 - (1+i)^(-1/3))] (annual compounding, quarterly convention) — approximate using
// M = P * n + P * n(n+1)/2 * i / 12 (simple interest approx) which is closer to typical Indian RD calculation
const calcRD = (monthly, months, ratePA) => {
    const i = ratePA / 100 / 12;
    // M = P * [((1+i)^n - 1) / i] * (1 + i) — standard monthly compounding formula
    const M = monthly * (((Math.pow(1 + i, months) - 1) / i) * (1 + i));
    return M;
};

const tenureOptions = [
    { months: 12, label: { en: "12 Months", mr: "१२ महिने" } },
    { months: 24, label: { en: "24 Months", mr: "२४ महिने" } },
    { months: 36, label: { en: "36 Months", mr: "३६ महिने" } },
    { months: 60, label: { en: "60 Months", mr: "६० महिने" } }
];

const rateLookup = {
    FD: { regular: { 12: 9.0, 24: 9.25, 36: 9.5, 60: 9.75 }, senior: { 12: 9.25, 24: 9.5, 36: 9.75, 60: 10.0 } },
    MIP: { regular: { 12: 8.0, 24: 8.25, 36: 8.5, 60: 8.75 }, senior: { 12: 8.25, 24: 8.5, 36: 8.75, 60: 9.0 } },
    RD: { regular: { 12: 10.0, 24: 10.5, 36: 11.0, 60: 11.5 }, senior: { 12: 10.0, 24: 10.5, 36: 11.0, 60: 11.5 } } // RD same rates
};

const Calculator = () => {
    const tr = useT();
    const { lang } = useLang();

    const [scheme, setScheme] = useState("FD");
    const [amount, setAmount] = useState(100000);
    const [months, setMonths] = useState(36);
    const [senior, setSenior] = useState(false);
    const [roi, setRoi] = useState("");

    const result = useMemo(() => {
        const tier = senior ? "senior" : "regular";
        const rate = roi ? roi : rateLookup[scheme][tier][months];
        if (scheme === "FD") {
            const maturity = calcFD(amount, months, rate);
            return { type: "fd", maturity, invested: amount, interest: maturity - amount, rate };
        }
        if (scheme === "MIP") {
            const monthly = Math.round(calcMIP(amount, rate));
            return { type: "mip", monthly, invested: amount, totalIncome: monthly * months, rate };
        }
        // RD: amount is monthly
        const maturity = calcRD(amount, months, rate);
        const invested = amount * months;
        return { type: "rd", maturity, invested, interest: maturity - invested, rate };
    }, [scheme, amount, months, senior, roi]);

    const amountLabel = scheme === "RD" ? tr(t.calculator.monthlyAmount) : tr(t.calculator.amount);

    return (
        <div>
            <section className="bg-gradient-to-br from-slate-900 to-red-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{tr(t.calculator.title)}</h1>
                    <p className="mt-4 text-slate-300 max-w-2xl text-base sm:text-lg">{tr(t.calculator.subtitle)}</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
                    {/* Inputs */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                        <h3 className="font-bold text-lg text-slate-900 mb-6 flex items-center gap-2">
                            <i className="fa-solid fa-calculator text-red-700" />
                            {lang === "mr" ? "आपली ठेव माहिती" : "Your Deposit Details"}
                        </h3>

                        {/* Scheme select */}
                        <label className="block text-sm font-semibold text-slate-700 mb-2">{tr(t.calculator.scheme)}</label>
                        <div className="grid grid-cols-3 gap-2 mb-6">
                            {[
                                { key: "FD", label: tr(t.deposits.tabs.fd) },
                                { key: "MIP", label: tr(t.deposits.tabs.mip) },
                                { key: "RD", label: tr(t.deposits.tabs.rd) }
                            ].map((s) => (
                                <button
                                    key={s.key}
                                    onClick={() => setScheme(s.key)}
                                    data-testid={`${TID.calcScheme}-${s.key}`}
                                    className={`px-3 py-3 rounded-lg text-sm font-semibold border transition-all ${
                                        scheme === s.key
                                            ? "bg-red-700 text-white border-red-700 shadow"
                                            : "bg-white text-slate-700 border-slate-200 hover:border-red-700"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>

                        {/* Amount */}
                        <label className="block text-sm font-semibold text-slate-700 mb-2">{amountLabel}</label>
                        <div className="relative mb-2">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">₹</span>
                            <input
                                type="number"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(Math.max(0, Number(e.target.value || 0)))}
                                data-testid={TID.calcAmount}
                                className="w-full pl-8 pr-3 py-3 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 text-base font-semibold text-slate-900 outline-none"
                            />
                        </div>
                        <input
                            type="range"
                            min={scheme === "RD" ? 500 : 5000}
                            max={scheme === "RD" ? 25000 : 2500000}
                            step={scheme === "RD" ? 500 : 5000}
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            className="w-full accent-red-700 mb-6"
                        />

                        {/* Tenure */}
                        <label className="block text-sm font-semibold text-slate-700 mb-2">{tr(t.calculator.tenure)}</label>
                        <div className="grid grid-cols-4 gap-2 mb-6">
                            {tenureOptions.map((tn) => (
                                <button
                                    key={tn.months}
                                    onClick={() => setMonths(tn.months)}
                                    data-testid={`${TID.calcTenure}-${tn.months}`}
                                    className={`px-2 py-3 rounded-lg text-sm font-semibold border transition-all ${
                                        months === tn.months
                                            ? "bg-amber-600 text-white border-amber-600 shadow"
                                            : "bg-white text-slate-700 border-slate-200 hover:border-amber-600"
                                    }`}
                                >
                                    {tr(tn.label)}
                                </button>
                            ))}
                        </div>

                        {/* Senior toggle */}
                        <label className="flex items-center gap-3 cursor-pointer select-none bg-amber-50 border border-amber-200 px-4 py-3 rounded-lg">
                            <input
                                type="checkbox"
                                checked={senior}
                                onChange={(e) => setSenior(e.target.checked)}
                                data-testid={TID.calcSenior}
                                className="w-5 h-5 accent-red-700"
                            />
                            <span className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                                <i className="fa-solid fa-user-clock" />
                                {tr(t.calculator.senior)}
                            </span>
                        </label>
                    </div>

                    {/* Result */}
                    <div className="bg-gradient-to-br from-red-700 via-red-700 to-amber-700 rounded-2xl text-white p-6 sm:p-8 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-red-900/30 blur-3xl" />

                        <div className="relative">
                            <div className="text-xs uppercase tracking-widest text-amber-200 font-bold mb-2">
                                {lang === "mr" ? "गणितांचा निकाल" : "Calculation Result"}
                            </div>

                            {result.type === "mip" ? (
                                <>
                                    <div className="text-sm text-amber-100">{tr(t.calculator.monthlyIncome)}</div>
                                    <div
                                        data-testid={TID.calcResult}
                                        className="text-5xl sm:text-6xl font-extrabold mt-1 mb-1 tracking-tight tabular-nums"
                                    >
                                        ₹ {fmtINR(result.monthly)}
                                    </div>
                                    <div className="text-sm text-amber-100 mb-6">
                                        {lang === "mr" ? "दर महिना" : "per month"}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="text-sm text-amber-100">{tr(t.calculator.result)}</div>
                                    <div
                                        data-testid={TID.calcResult}
                                        className="text-5xl sm:text-6xl font-extrabold mt-1 mb-6 tracking-tight tabular-nums"
                                    >
                                        ₹ {fmtINR(result.maturity)}
                                    </div>
                                </>
                            )}

                            <div className="grid grid-cols-2 gap-3 mb-6">
                                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                                    <div className="text-xs text-amber-100 uppercase tracking-wider font-semibold mb-1">
                                        {tr(t.calculator.totalInvested)}
                                    </div>
                                    <div data-testid={TID.calcResultInvested} className="text-xl sm:text-2xl font-bold tabular-nums">
                                        ₹ {fmtINR(result.invested)}
                                    </div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                                    <div className="text-xs text-amber-100 uppercase tracking-wider font-semibold mb-1">
                                        {result.type === "mip" ? (lang === "mr" ? "एकूण उत्पन्न" : "Total Income") : tr(t.calculator.totalInterest)}
                                    </div>
                                    <div data-testid={TID.calcResultInterest} className="text-xl sm:text-2xl font-bold tabular-nums">
                                        ₹ {fmtINR(result.type === "mip" ? result.totalIncome : result.interest)}
                                    </div>
                                </div>
                            </div>

                            <div
                                data-testid={TID.calcRate}
                                className="inline-flex items-center gap-2 bg-amber-400 text-slate-900 px-4 py-2 rounded-full text-sm font-bold"
                            >
                                {/* <i className="fa-solid fa-percent" /> */}
                                <input
                                    type="number"
                                    step="0.25"
                                    min="0"
                                    value={roi}
                                    onChange={(e) => setRoi(e.target.value === "" ? "" : parseFloat(e.target.value))}
                                    data-testid={TID.calcRoi}
                                    placeholder={String(rateLookup[scheme][senior ? "senior" : "regular"][months])}
                                    className="bg-transparent border-none focus:ring-0 p-0 w-14 font-bold text-slate-900 placeholder-slate-900/70 outline-none"
                                />
                                % PA {senior && (lang === "mr" ? "(ज्येष्ठ)" : "(Senior)")}
                            </div>

                            <p className="text-xs text-amber-100 mt-6 leading-relaxed">
                                <i className="fa-solid fa-circle-info mr-1" />
                                {lang === "mr"
                                    ? "वरील आकडे केवळ अंदाजासाठी आहेत. प्रत्यक्ष दर व अटी कार्यालयात पुष्टी करा."
                                    : "Figures are indicative. Please confirm exact rates and terms at our branch."}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Calculator;
